import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { Star } from '@sorare/core/src/atoms/icons/Star';
import { StarEmpty } from '@sorare/core/src/atoms/icons/StarEmpty';
import Accordion from '@sorare/core/src/atoms/layout/Accordion';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';

import { useCreateCompetitionSubscription } from 'components/rivals/RivalsMatchesDateLayout/useCreateCompetitionSubscription';
import { useDeleteCompetitionSubscription } from 'components/rivals/RivalsMatchesDateLayout/useDeleteCompetitionSubscription';

import { LeagueSectionAccordion_footballRivalsLeagueGame } from './__generated__/index.graphql';

const SectionTitle = styled(Horizontal)`
  width: 100%;
`;
const FlagImg = styled.img`
  grid-area: flag;
  width: var(--triple-unit);
  object-fit: contain;
`;
const NamesWrapper = styled(Vertical)`
  align-items: flex-start;
`;
const CountryName = styled(LabelS)`
  text-transform: uppercase;
`;
const GreyWrapper = styled.span`
  color: var(--c-neutral-600);
`;
const StyledLinkOther = styled(LinkOther)`
  margin-left: auto;
`;

type Props = {
  league: any;
  startsOpen?: boolean;
  children: ReactNode;
};

export const LeagueSectionAccordion = ({
  league,
  startsOpen,
  children,
}: Props) => {
  const [subscribe] = useCreateCompetitionSubscription();
  const [unsubscribe] = useDeleteCompetitionSubscription();
  const { showNotification } = useSnackNotificationContext();

  const { competition, games } = league;
  const { flagUrl, name: countryName } = competition.country || {};
  const subscriptionSlug = competition.currentUserSubscription?.slug;
  const isFavorite = !!subscriptionSlug;

  const onFavoriteClick = () => {
    if (isFavorite) {
      unsubscribe({ subscriptionSlug, competitionSlug: competition.slug });
      showNotification('competitionUnsubscribed', {
        displayName: competition.displayName,
      });
    } else {
      subscribe(competition.slug);
      showNotification('competitionSubscribed', {
        displayName: competition.displayName,
      });
    }
  };

  return (
    <Accordion
      startsOpen={startsOpen}
      title={
        <SectionTitle gap={2}>
          {flagUrl && <FlagImg src={competition.country?.flagUrl} alt="" />}
          <NamesWrapper gap={0}>
            {countryName && (
              <CountryName as="div" color="var(--c-neutral-600)">
                {countryName}
              </CountryName>
            )}
            <BodyS as="div" bold>
              {competition.displayName}
            </BodyS>
          </NamesWrapper>
          <StyledLinkOther
            as={IconButton}
            color="transparent"
            onClick={onFavoriteClick}
          >
            {isFavorite ? (
              <Star />
            ) : (
              <GreyWrapper>
                <StarEmpty />
              </GreyWrapper>
            )}
          </StyledLinkOther>
          <BodyS as="div" color="var(--c-neutral-600)">
            <FormattedNumber value={games.length} />
          </BodyS>
        </SectionTitle>
      }
      noTopPadding
    >
      <Vertical gap={2}>{children}</Vertical>
    </Accordion>
  );
};

LeagueSectionAccordion.fragments = {
  footballRivalsLeagueGame: gql`
    fragment LeagueSectionAccordion_footballRivalsLeagueGame on FootballRivalsLeagueGame {
      competition {
        slug
        displayName
        country {
          slug
          flagUrl
          name
        }
        currentUserSubscription {
          slug
        }
      }
    }
  ` as TypedDocumentNode<LeagueSectionAccordion_footballRivalsLeagueGame>,
};
